// noinspection DuplicatedCode
import React, { useEffect, useState, useCallback, useMemo } from "react";
import "react-datepicker/dist/react-datepicker.css";

import { Colors } from "../../../assets/Colors";
import { Card } from "@material-tailwind/react";
import { HrdInputGenerator } from "../../../components/form/HrdInputGenerator";
import { useForm } from "../../../components/isRequired";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchGet, fetchPost } from "../../../fetchers";
import { toast } from "react-toastify";
import { maxPageGlobal } from "../../../assets/config";
import { NewDownloadExcel } from "../../../components/tablecard/NewTableMechanise";
import Skeltons from "../../../components/loading/Skeltons";
import debounce from "lodash/debounce";

const MonitoringPurchaseRequisiton = () => {
  // =========================
  // form
  // =========================
  const initialState = {
    Bulan: new Date(),
    BisnisUnitID: "U0002",
    Bagian: "B",
  };

  const validations = [];

  const { values, errors, touched, customHandler } = useForm(
    initialState,
    validations
  );

  // =========================
  // pagination & search
  // =========================
  const [page, setPage] = useState(1);
  const [limit] = useState(maxPageGlobal);
  const [totalPage, setTotalPage] = useState(1);

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false); // Menambahkan state untuk indikasi searching
  const [reasonModal, setReasonModal] = useState({ show: false, text: "", id: "" });

  // =========================
  // parameter API
  // =========================
  const [param, setParam] = useState({
    BisnisUnitID: "U0002",
    Bulan: (new Date().getMonth() + 1).toString(),
    Tahun: new Date().getFullYear().toString(),
    Bagian: "B",
  });

  // =========================
  // debounce search
  // =========================
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchQuery(value);
        setPage(1);
        setIsSearching(false); // Set searching selesai ketika debounce selesai
      }, 800), // Mengubah delay menjadi 800ms seperti contoh
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  // =========================
  // fetch list
  // =========================
  const {
    data: monitoringData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      "monitoring-pr",
      param.BisnisUnitID,
      param.Bulan,
      param.Tahun,
      param.Bagian,
      page,
      limit,
      searchQuery,
    ],
    queryFn: () =>
      fetchGet(
        `Procurement/Monitoring/PurchaseRequisition/${param.BisnisUnitID}/${param.Bulan}/${param.Tahun}/${param.Bagian}/${encodeURIComponent(
          searchQuery
        )}?page=${page}&limit=${limit}&search=${encodeURIComponent(
          searchQuery
        )}`
      ),
    keepPreviousData: true,
    staleTime: 30000, // Menambahkan staleTime seperti contoh
    onSuccess: (res) => {
      if (res?.meta?.total_page) {
        setTotalPage(res.meta.total_page);
      } else {
        setTotalPage(1);
      }
    },
  });

  // =========================
  // kirim / terima
  // =========================
  const KirimTerima = useMutation((formData) =>
    fetchPost("Procurement/PurchaseRequisition/Terima", formData)
  );

  // =========================
  // handler search
  // =========================
  const handleSearchChange = useCallback(
    (input) => {
      const value =
        typeof input === "string"
          ? input
          : input?.target?.value || "";

      setSearchInput(value);
      debouncedSetSearch.cancel(); // Membatalkan debounce sebelumnya

      if (value.trim() === "") {
        // Jika input kosong, langsung reset search
        setSearchQuery("");
        setPage(1);
        setIsSearching(false);
      } else {
        // Jika ada input, set searching state dan trigger debounce
        setIsSearching(true);
        debouncedSetSearch(value);
      }
    },
    [debouncedSetSearch]
  );

  // =========================
  // filter button
  // =========================
  const filterApi = useCallback(() => {
    debouncedSetSearch.cancel();

    setSearchInput("");
    setSearchQuery("");
    setPage(1);
    setIsSearching(false); // Reset searching state

    setParam({
      BisnisUnitID: values.BisnisUnitID,
      Bulan: (new Date(values.Bulan).getMonth() + 1).toString(),
      Tahun: new Date(values.Bulan).getFullYear().toString(),
      Bagian: values.Bagian || "B",
    });
  }, [values, debouncedSetSearch]);

  // =========================
  // pagination handler
  // =========================
  const nextPage = useCallback(() => {
    if (page < totalPage) setPage((p) => p + 1);
  }, [page, totalPage]);

  const prevPage = useCallback(() => {
    if (page > 1) setPage((p) => p - 1);
  }, [page]);

  // =========================
  // action kirim / terima
  // =========================
  const kirimTerima = useCallback(
    (id) => {
      KirimTerima.mutate(
        { PurchaseRequisitionID: id },
        {
          onSuccess: () => {
            toast.success("Berhasil diproses", {
              position: "top-center",
              autoClose: 1000,
            });
          },
        }
      );
    },
    [KirimTerima]
  );

  // =========================
  // table header
  // =========================
  const header = useMemo(
    () => [
      { name: "No. MSR", key: "PurchaseRequisitionID" },
      { name: "Tanggal MSR", key: "DatePR", modifiers: "Date" },
      { name: "Keterangan", key: "Remarks", modifiers: "Text" },
      { name: "Bagian", key: "NamaBagian", modifiers: "Text" },
      { name: "Pembuat", key: "NamaKaryawan", modifiers: "Text" },
      { name: "Pemohon", key: "Requester", modifiers: "Text" },
      { name: "Tipe", key: "Tipe", modifiers: "Text" },
      { name: "Status", key: "STATUS", modifiers: "Text" },
      { name: "Pemakaian", key: "Jumlah", modifiers: "Number" },
      { name: "Approval", key: "Approval", modifiers: "ApprovalPRC" },
      {
        name: "No. Pengeluaran Barang",
        key: "GoodsExpenditureID",
        modifiers: "Text",
      },
      {
        name: "Status Pengeluaran",
        key: "StatusPengeluaran",
        modifiers: "Text",
      },
      { name: "Alasan Penolakan", key: "KeteranganTolak", modifiers: "Text" },
      { name: "Action", key: "LihatPR", modifiers: "LihatPR" },
    ],
    []
  );

  const showLoading = isLoading && !monitoringData;

  return (
    <>
      <div className="md:ml-64">
        <div
          style={{ background: Colors.primary }}
          className="pt-14 pb-16 px-3 md:px-8 h-auto"
        >
          <div className="container mx-auto max-w-full" />
        </div>

        <div className="px-3 md:px-8 h-auto -mt-32">
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 px-4">
              <Card>
                {/* ================= filter ================= */}
                <div className="flex flex-row flex-wrap items-start justify-start w-full px-2 py-2">
                  <div className="w-1/4">
                    <HrdInputGenerator
                      type={"BisnisUnit"}
                      name={"BisnisUnitID"}
                      title={"BisnisUnit"}
                      value={values}
                      required={true}
                      onChange={customHandler}
                      touched={touched}
                      errors={errors}
                      size={"full"}
                    />
                  </div>

                  <div className="w-1/4">
                    <HrdInputGenerator
                      type={"bulan"}
                      name={"Bulan"}
                      title={"Bulan"}
                      value={values}
                      required={true}
                      onChange={customHandler}
                      touched={touched}
                      errors={errors}
                      size={"full"}
                    />
                  </div>

                  <div className="w-1/4">
                    <HrdInputGenerator
                      type={"Bagian"}
                      name={"Bagian"}
                      title={"Bagian"}
                      value={values}
                      required={true}
                      onChange={customHandler}
                      touched={touched}
                      errors={errors}
                      size={"full"}
                    />
                  </div>

                  <div className="w-1/4">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                      Buka data
                    </label>

                    <button
                      type="button"
                      onClick={filterApi}
                      className="text-main-100 hover:text-white border border-main-100 hover:bg-main-100 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-7 py-2 text-center mr-2 mb-2"
                    >
                      Buka
                    </button>
                  </div>
                </div>

                {/* ================= table ================= */}
                <div className="flex flex-col content-center">
                  {showLoading ? (
                    <div className="w-full pt-0 items-left">
                      <Skeltons />
                    </div>
                  ) : monitoringData ? (
                    <>
                      <NewDownloadExcel
                        data={monitoringData} // jangan .data
                        header={header}
                        menuController={handleSearchChange}
                        value={searchInput}
                        name={"Nama"}
                        title={"MSR"}
                        keys={`${page}-${searchQuery}`}
                        kirimTerima={kirimTerima}
                        isFetching={isFetching}
                        onShowReason={(row) =>
                          setReasonModal({
                            show: true,
                            text: row?.KeteranganTolak || "",
                            id: row?.PurchaseRequisitionID || "",
                          })
                        }
                      />

                      {reasonModal.show && (
                        <div
                          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
                          onClick={() =>
                            setReasonModal({ show: false, text: "", id: "" })
                          }
                        >
                          <div
                            className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="text-red-600 font-semibold text-base mb-1">
                              Alasan Penolakan
                            </div>
                            <p className="text-xs text-gray-500 mb-3">
                              {reasonModal.id}
                            </p>
                            <p className="text-sm text-gray-700 whitespace-pre-line border border-gray-200 rounded-lg p-3 bg-gray-50 min-h-[60px]">
                              {reasonModal.text && reasonModal.text.trim() !== ""
                                ? reasonModal.text
                                : "Tidak ada alasan tercatat (penolakan sebelum fitur alasan tersedia)."}
                            </p>
                            <div className="flex justify-end mt-4">
                              <button
                                onClick={() =>
                                  setReasonModal({ show: false, text: "", id: "" })
                                }
                                className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg"
                              >
                                Tutup
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* ================= Search Status Indicators ================= */}
                      {searchInput !== searchQuery && searchInput && (
                        <div className="px-4 text-xs text-blue-500">
                          Mengetik "{searchInput}"...
                        </div>
                      )}

                      {searchQuery && !isSearching && (
                        <div className="px-4 text-xs text-green-600">
                          Menampilkan hasil untuk "{searchQuery}"
                        </div>
                      )}

                      {/* ================= pagination ================= */}
                      <div className="flex flex-row justify-end items-center gap-4 px-4 py-4">
                        <button
                          onClick={prevPage}
                          disabled={page === 1}
                          className="border px-4 py-1 rounded disabled:opacity-50"
                        >
                          Prev
                        </button>

                        <span className="text-sm">
                          Page {page} / {totalPage}
                          {isFetching && (
                            <span className="ml-2 text-xs text-gray-400">
                              memuat...
                            </span>
                          )}
                        </span>

                        <button
                          onClick={nextPage}
                          disabled={page >= totalPage}
                          className="border px-4 py-1 rounded disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </>
                  ) : null}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MonitoringPurchaseRequisiton;